import addFlexPaneResults from '../actions/addFlexPaneResults';
import clearFlexPaneResults from '../actions/clearFlexPaneResults';
import setFlexPaneResultsLoading from '../actions/setFlexPaneResultsLoading';
import datapoints, { logCortanaUsage } from '../datapoints';
import executeDoradoRuntime from '../service/executeDoradoRuntime';
import store from '../store/cortanaComposeStore';
import cortanaComposeStore from '../store/cortanaComposeStore';
import CortanaComposeViewState from '../store/schema/CortanaComposeViewState';
import DoradoActionParameter from '../store/schema/DoradoActionParameter';
import DoradoRuntimeResult from '../store/schema/DoradoRuntimeResult';
import parseHeroCard from '../utils/parseHeroCard';
import renderHeroCardToHtml from '../utils/renderHeroCardToHtml';
import { observer } from 'mobx-react';
import { DefaultButton, CommandButton } from 'office-ui-fabric-react/lib/Button';
import { ITextField } from 'office-ui-fabric-react/lib/components/TextField';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { Image } from 'office-ui-fabric-react/lib/Image';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { lazyHideFlexPane, lazyShowFlexPane } from 'owa-flex-pane';
import { keydownHandler } from 'owa-hotkeys';
import { newMessageV3 } from 'owa-mail-actions';
import strings from 'owa-strings/owa-cortana-strings';
import { trace } from 'owa-trace';
import * as React from 'react';

export interface CortanaComposeFlexPaneProps {
    requiresUserInput: boolean;
    action: any;
    actionParameters?: DoradoActionParameter[];
    doradoRuntimeResults?: any;
    viewState: CortanaComposeViewState;
}

const styles = require('./CortanaComposeFlexPane.scss');

const IMAGE_SIZE = 240;

@observer
export default class CortanaComposeFlexPane extends React.Component<
    CortanaComposeFlexPaneProps,
    {}
> {
    private textField: ITextField;

    render() {
        return (
            <FocusZone
                direction={FocusZoneDirection.vertical}
                className={styles.root}
                isCircularNavigation={true}>
                {
                    <Label data-is-focusable={true}>
                        {' '}
                        {this.props.action.value.Payload.Description}{' '}
                    </Label>
                }

                {this.props.requiresUserInput &&
                    this.props.actionParameters.map(
                        (actionParameter: DoradoActionParameter, index: number) => {
                            return this.renderActionParameter(actionParameter, index);
                        }
                    )}
                {this.props.requiresUserInput && this.renderSearchButton()}

                {this.props.viewState.flexPaneResultsLoading && (
                    <Spinner className={styles.listItem} size={SpinnerSize.large} />
                )}

                {!this.props.viewState.flexPaneResultsLoading &&
                    this.props.viewState.flexPaneResults.map((item, index) => {
                        return this.renderDoradoRuntimeResult(item, index);
                    })}

                {this.renderFeedbackLink()}
            </FocusZone>
        );
    }

    componentDidMount() {
        if (this.textField) {
            this.textField.focus();
        }

        if (this.props.doradoRuntimeResults) {
            this.parseDoradoRuntimeResults(this.props.doradoRuntimeResults);
        }
    }

    @keydownHandler('escape')
    onClose(evt: KeyboardEvent) {
        evt.stopPropagation();
        lazyHideFlexPane.importAndExecute();
    }

    private renderActionParameter(
        actionParameter: DoradoActionParameter,
        index: number
    ): JSX.Element {
        if (actionParameter.requiresUserInput) {
            return (
                <TextField
                    key={index}
                    ariaLabel={actionParameter.name}
                    componentRef={ref => (this.textField = ref)}
                    onChanged={(newValue: string) => {
                        actionParameter.value = newValue;
                    }}
                    onKeyDown={this.onKeyDownInActionParameterTextField}
                />
            );
        } else {
            return null;
        }
    }

    private onKeyDownInActionParameterTextField = (evt: React.KeyboardEvent<EventTarget>) => {
        switch (evt.keyCode) {
            case KeyCodes.enter:
                this.doSearch();
        }
    };

    private renderSearchButton(): JSX.Element {
        return (
            <DefaultButton
                text={strings.searchButtonText}
                className={styles.searchButton}
                onClick={this.doSearch}
            />
        );
    }

    private renderFeedbackLink(): JSX.Element {
        return (
            <CommandButton
                onClick={this.createMailForFeedback}
                text={strings.feedbackLink}
                className={styles.feedbackLink}
            />
        );
    }

    // This mechanism for feedback it temporary and will not rollout past SDF
    private createMailForFeedback = () => {
        const toRecipients = [
            {
                EmailAddress: 'outlookcortanafb@microsoft.com',
                Name: 'outlookcortanafb@microsoft.com',
            },
        ];

        newMessageV3(
            'CortanaCompose',
            null /*groupId*/,
            toRecipients,
            'Feedback for Cortana in Outlook',
            ''
        );
    };

    private renderDoradoRuntimeResult = (
        doradoRuntimeResult: DoradoRuntimeResult,
        index: number | undefined
    ): JSX.Element => {
        return (
            <div
                className={styles.listItem}
                data-is-focusable={true}
                data-selection-index={index}
                key={index}
                data-selection-toggle={true}
                onClick={() => this.onResultClicked(doradoRuntimeResult)}>
                <Label>{doradoRuntimeResult.heroCard.title}</Label>
                {doradoRuntimeResult.heroCard.images && (
                    <Image
                        className={styles.listItem}
                        src={doradoRuntimeResult.heroCard.images[0]}
                        width={IMAGE_SIZE}
                    />
                )}
            </div>
        );
    };

    private onResultClicked = (result: DoradoRuntimeResult) => {
        trace.info('[CortanaCompose] result clicked');
        logCortanaUsage(
            datapoints.CortanaComposeContentInserted.datapoint.name,
            datapoints.CortanaComposeContentInserted.datapoint.customData(
                store.currentSearchCorrelationId
            )
        );

        if (result.heroCard && store.viewState.pluginCallbacks.insertContent) {
            store.viewState.pluginCallbacks.insertContent(
                renderHeroCardToHtml(result.heroCard, true /* include buttons */)
            );
            lazyHideFlexPane.importAndExecute();
        }
    };

    private doSearch = async () => {
        setFlexPaneResultsLoading(this.props.viewState, true);
        clearFlexPaneResults();

        logCortanaUsage(
            datapoints.CortanaComposeRuntimeExecuted.datapoint.name,
            datapoints.CortanaComposeRuntimeExecuted.datapoint.customData(
                store.currentSearchCorrelationId
            )
        );

        const runtimeResult = await executeDoradoRuntime(
            this.props.action,
            this.props.actionParameters,
            store.currentSearchCorrelationId
        );

        if (runtimeResult) {
            this.parseDoradoRuntimeResults(runtimeResult);
        } else {
            // todo: better error handling here
            trace.info('[CortanaComposeFlexPane] no DoradoRuntime results');
        }
    };

    private parseDoradoRuntimeResults = (doradoRuntimeResults: any) => {
        setFlexPaneResultsLoading(this.props.viewState, false);
        const parsedResults = [];
        doradoRuntimeResults.forEach(result => {
            if (result.attachments) {
                result.attachments.forEach(attachment => {
                    const heroCard = parseHeroCard(attachment);
                    if (heroCard) {
                        parsedResults.push({ heroCard: heroCard });
                    }
                });
            }
        });

        if (parsedResults.length > 0) {
            addFlexPaneResults(this.props.viewState, parsedResults);
        }
    };
}

export function openFlexPaneForAction(
    requiresUserInput: boolean,
    action: any,
    actionParameters: DoradoActionParameter[]
) {
    clearFlexPaneResults();
    lazyShowFlexPane.importAndExecute(
        <CortanaComposeFlexPane
            requiresUserInput={requiresUserInput}
            viewState={cortanaComposeStore.viewState}
            action={action}
            actionParameters={actionParameters}
        />,
        {
            headerText: () => action.title,
            overlayOnSmallWidth: false,
        }
    );
}

export function openFlexPaneWithResults(action: any, doradoRuntimeResults: any) {
    clearFlexPaneResults();
    lazyShowFlexPane.importAndExecute(
        <CortanaComposeFlexPane
            requiresUserInput={false}
            viewState={cortanaComposeStore.viewState}
            action={action}
            doradoRuntimeResults={doradoRuntimeResults}
        />,
        {
            headerText: () => action.title,
            overlayOnSmallWidth: false,
        }
    );
}
